import { useTranslation } from 'next-i18next/pages';
import type { ReactNode } from 'react';
import type { UrlObject } from 'url';

import Link from '@/routing/AppLink';

import {
  IconChevronLeftRound,
  IconChevronRightRound,
} from '@/components/apps/ui/icon';

type Props = {
  /** 활성화 페이지 index (첫번째 페이지 index값 0) */
  forcePage: number | undefined;
  /** 전체 페이지 수 */
  pageCount: number;
  /** 현재 페이지를 중심으로 양쪽에 보여줄 페이지의 수 */
  pageRange?: number;
  /** tw 사용 */
  className?: string;
  /** 핸들러 함수 */
  onPageChangeHandler?: (selectedNumber: { selected: number }) => void;
  /** 검색엔진과 CDN이 발견할 수 있는 페이지 URL */
  getPageHref?: (page: number) => string | UrlObject;
};

type PaginationToken = number | 'ellipsis';

/*
 * 배경 유틸리티는 base 가 아니라 idle/active 한쪽에만 둔다.
 * tailwind 를 important 로 임포트하고 있어 base 의 bg-transparent 와 active 의
 * bg-apps-corePrimary01 이 모두 !important 가 되고, 승자는 className 순서가 아니라
 * 생성된 CSS 순서로 정해져 base 가 이겨버린다.
 */
const BUTTON_BASE =
  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 text-base font-medium leading-[150%] no-underline transition-[background-color,color] duration-200 lg:h-10 lg:w-10';
/** active 일 때 hover 배경이 이기지 않도록 hover 유틸을 아예 붙이지 않는다 */
const BUTTON_IDLE =
  'bg-transparent text-apps-coreGrayBlack hover:bg-apps-coreGray07';
const BUTTON_ACTIVE = 'bg-apps-corePrimary01 text-apps-coreGrayWhite';
const CELL = 'grid h-8 w-8 place-items-center lg:h-10 lg:w-10';
const ICON =
  'inline-flex h-4 w-4 lg:h-6 lg:w-6 [&_svg]:h-full [&_svg]:w-full';

type PageControlProps = {
  active?: boolean;
  children: ReactNode;
  disabled?: boolean;
  href?: string | UrlObject;
  label: string;
  onSelect: () => void;
};

function PageControl({
  active = false,
  children,
  disabled = false,
  href,
  label,
  onSelect,
}: PageControlProps) {
  const className = `${BUTTON_BASE} ${active ? BUTTON_ACTIVE : BUTTON_IDLE}`;

  if (href && !active && !disabled) {
    return (
      <Link
        href={href}
        prefetch={false}
        className={className}
        aria-label={label}
      >
        {children}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      aria-current={active ? 'page' : undefined}
      aria-label={label}
      disabled={disabled}
      onClick={onSelect}
    >
      {children}
    </button>
  );
}

function buildPaginationTokens(
  currentPage: number,
  pageCount: number,
  pageRange: number,
): PaginationToken[] {
  const visiblePages = new Set([1, pageCount]);

  for (
    let page = Math.max(1, currentPage - pageRange);
    page <= Math.min(pageCount, currentPage + pageRange);
    page += 1
  ) {
    visiblePages.add(page);
  }

  const pages = [...visiblePages].sort((a, b) => a - b);
  return pages.flatMap((page, index) => {
    const previousPage = pages[index - 1];
    return previousPage && page - previousPage > 1 ? ['ellipsis', page] : page;
  });
}

export default function Pagination({
  forcePage,
  pageCount,
  pageRange = 5,
  onPageChangeHandler,
  getPageHref,
  className,
}: Props) {
  const { t } = useTranslation('common');
  const currentPage = Math.min(Math.max(forcePage ?? 1, 1), pageCount);
  const tokens = buildPaginationTokens(currentPage, pageCount, pageRange);
  const selectPage = (page: number) => {
    if (page !== currentPage) {
      onPageChangeHandler?.({ selected: page - 1 });
    }
  };

  if (!pageCount) {
    return null;
  }

  const itemClass = (isHidden: boolean) =>
    isHidden ? 'pointer-events-none opacity-0' : 'pointer-events-auto opacity-100';

  return (
    <nav aria-label={t('페이지 이동')}>
      <ul
        className={`flex list-none justify-center gap-x-1 overflow-hidden py-5 ${className ?? ''}`}
      >
        <li className={itemClass(currentPage === 1)}>
          <PageControl
            label={t('이전 페이지')}
            disabled={currentPage === 1}
            href={getPageHref?.(currentPage - 1)}
            onSelect={() => selectPage(currentPage - 1)}
          >
            <span className={ICON} aria-hidden="true">
              <IconChevronLeftRound size={24} />
            </span>
          </PageControl>
        </li>

        {tokens.map((token, index) =>
          token === 'ellipsis' ? (
            <li key={`ellipsis-${index}`} aria-hidden="true">
              <span className={CELL}>…</span>
            </li>
          ) : (
            <li key={token}>
              <PageControl
                active={token === currentPage}
                label={t('페이지 {{page}}', { page: token })}
                href={getPageHref?.(token)}
                onSelect={() => selectPage(token)}
              >
                {token}
              </PageControl>
            </li>
          ),
        )}

        <li className={itemClass(currentPage === pageCount)}>
          <PageControl
            label={t('다음 페이지')}
            disabled={currentPage === pageCount}
            href={getPageHref?.(currentPage + 1)}
            onSelect={() => selectPage(currentPage + 1)}
          >
            <span className={ICON} aria-hidden="true">
              <IconChevronRightRound size={24} />
            </span>
          </PageControl>
        </li>
      </ul>
    </nav>
  );
}
